import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'product';
  noIndex?: boolean;
  canonicalUrl?: string;
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  structuredData?: object;
}

const defaultSEO = {
  siteName: 'Sistema de Atendimento WhatsApp',
  title: 'Sistema de Atendimento WhatsApp',
  description: 'Sistema completo de atendimento ao cliente via WhatsApp. Gerencie tickets, automatize respostas, crie fluxos de atendimento e aumente a produtividade da sua equipe.',
  keywords: 'whatsapp, atendimento, tickets, chatbot, automacao, crm, suporte ao cliente, helpdesk, omnichannel, customer service',
  image: '/og-image.png',
  url: window.location.origin,
  locale: 'pt_BR',
  twitterHandle: '@atendimento',
  author: 'Sistema de Atendimento',
};

export default function SEO({
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  noIndex = false,
  canonicalUrl,
  author,
  publishedTime,
  modifiedTime,
  structuredData,
}: SEOProps) {
  const seoTitle = title ? `${title} | ${defaultSEO.siteName}` : defaultSEO.title;
  const seoDescription = description || defaultSEO.description;
  const seoKeywords = keywords || defaultSEO.keywords;
  const seoImage = image || defaultSEO.image;
  const seoUrl = url || defaultSEO.url;
  const seoAuthor = author || defaultSEO.author;

  // Default structured data for organization
  const defaultStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: defaultSEO.siteName,
    description: seoDescription,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'BRL',
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.8',
      ratingCount: '150',
    },
    author: {
      '@type': 'Organization',
      name: defaultSEO.siteName,
      url: defaultSEO.url,
    },
  };

  const jsonLd = structuredData || defaultStructuredData;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{seoTitle}</title>
      <meta name="description" content={seoDescription} />
      <meta name="keywords" content={seoKeywords} />
      <meta name="author" content={seoAuthor} />
      <meta name="robots" content={noIndex ? 'noindex, nofollow' : 'index, follow'} />
      <meta name="googlebot" content={noIndex ? 'noindex, nofollow' : 'index, follow'} />

      {/* Canonical URL */}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={defaultSEO.siteName} />
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:image" content={seoImage} />
      <meta property="og:image:alt" content={seoTitle} />
      <meta property="og:url" content={seoUrl} />
      <meta property="og:locale" content={defaultSEO.locale} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={defaultSEO.twitterHandle} />
      <meta name="twitter:creator" content={defaultSEO.twitterHandle} />
      <meta name="twitter:title" content={seoTitle} />
      <meta name="twitter:description" content={seoDescription} />
      <meta name="twitter:image" content={seoImage} />
      <meta name="twitter:image:alt" content={seoTitle} />

      {/* Article specific meta tags */}
      {type === 'article' && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === 'article' && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {type === 'article' && (
        <meta property="article:author" content={seoAuthor} />
      )}

      {/* Additional SEO Tags */}
      <meta name="format-detection" content="telephone=no" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      <meta name="apple-mobile-web-app-title" content={defaultSEO.siteName} />

      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(jsonLd)}
      </script>
    </Helmet>
  );
}

// Pre-configured SEO components for common pages
export const pageSEOConfig = {
  dashboard: {
    title: 'Dashboard',
    description: 'Painel de controle com visao geral de tickets, atendimentos e metricas de desempenho da sua equipe.',
    keywords: 'dashboard, metricas, analytics, kpi, relatorios, desempenho',
  },
  tickets: {
    title: 'Tickets',
    description: 'Gerencie todos os tickets de atendimento. Visualize conversas, atribua agentes e resolva solicitacoes.',
    keywords: 'tickets, atendimento, suporte, conversas, whatsapp, chamados',
  },
  connections: {
    title: 'Conexoes',
    description: 'Gerencie suas conexoes do WhatsApp. Conecte multiplos numeros e monitore o status de cada conexao.',
    keywords: 'conexoes, whatsapp, qr code, multiplos numeros, integracao',
  },
  contacts: {
    title: 'Contatos',
    description: 'Base de contatos do seu sistema. Gerencie informacoes de clientes e historico de conversas.',
    keywords: 'contatos, clientes, leads, base de dados, crm',
  },
  queues: {
    title: 'Filas',
    description: 'Configure filas de atendimento para organizar e distribuir tickets entre sua equipe.',
    keywords: 'filas, distribuicao, atendimento, setores, departamentos',
  },
  quickReplies: {
    title: 'Respostas Rapidas',
    description: 'Crie e gerencie respostas rapidas para agilizar o atendimento ao cliente.',
    keywords: 'respostas rapidas, templates, mensagens prontas, atalhos',
  },
  scheduledMessages: {
    title: 'Mensagens Agendadas',
    description: 'Agende envio de mensagens para seus contatos. Programe campanhas e lembretes automaticos.',
    keywords: 'mensagens agendadas, automacao, campanhas, agendamento, lembretes',
  },
  tags: {
    title: 'Tags',
    description: 'Organize tickets e contatos com tags personalizadas. Facilite a categorizacao e busca.',
    keywords: 'tags, etiquetas, categorizacao, organizacao, filtros',
  },
  crm: {
    title: 'CRM',
    description: 'Gestao de relacionamento com clientes. Acompanhe o funil de vendas e oportunidades.',
    keywords: 'crm, vendas, funil, oportunidades, relacionamento, leads',
  },
  flows: {
    title: 'Fluxos',
    description: 'Construa fluxos de atendimento automatizado com nosso editor visual intuitivo.',
    keywords: 'fluxos, automacao, chatbot, workflow, builder, drag and drop',
  },
  aiAgents: {
    title: 'Agentes IA',
    description: 'Configure agentes de inteligencia artificial para automatizar atendimentos.',
    keywords: 'ia, inteligencia artificial, chatbot, automacao, machine learning',
  },
  knowledgeBase: {
    title: 'Base de Conhecimento',
    description: 'Centralize documentos e informacoes para alimentar seus agentes de IA.',
    keywords: 'base conhecimento, documentos, faq, perguntas frequentes, wiki',
  },
  users: {
    title: 'Usuarios',
    description: 'Gerencie usuarios e permissoes do sistema. Adicione agentes e configure acessos.',
    keywords: 'usuarios, agentes, permissoes, equipe, acessos, roles',
  },
  settings: {
    title: 'Configuracoes',
    description: 'Configure preferencias do sistema, integracao e personalizacao.',
    keywords: 'configuracoes, preferencias, integracao, personalizacao, setup',
  },
  integrations: {
    title: 'Integracoes',
    description: 'Conecte o sistema com outras ferramentas e servicos. APIs e webhooks disponiveis.',
    keywords: 'integracoes, api, webhooks, zapier, n8n, automacao',
  },
  login: {
    title: 'Entrar',
    description: 'Acesse sua conta e gerencie seus atendimentos via WhatsApp.',
    keywords: 'login, entrar, acesso, conta',
    noIndex: true,
  },
  register: {
    title: 'Criar Conta',
    description: 'Crie sua conta e comece a atender seus clientes via WhatsApp.',
    keywords: 'cadastro, registro, criar conta, trial',
  },
};
